import { orderTypeLabel, orderDietaryLabel } from "@/lib/admin/order-type";
import { OrderOriginReviewPanel } from "@/components/admin/OrderOriginReviewPanel";
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  orderSourceLabel,
  manualPaymentLabel,
  entryModeLabel,
  ORDER_SOURCE_FILTER_OPTIONS,
  ENTRY_MODES,
} from "@/lib/admin/ops-sources";
import {
  ChefHat,
  CircleDot,
  CreditCard,
  Eye,
  History,
  Layers,
  ListOrdered,
  MapPin,
  Pencil,
  Printer,
  Receipt,
  StickyNote,
  Truck,
  User,
  Utensils,
  UtensilsCrossed,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Checkbox } from "@/components/ui/checkbox";
import { OrdersBulkToolbar } from "@/components/admin/OrdersBulkToolbar";
import { OrdersBulkDispatchDialog } from "@/components/admin/OrdersBulkDispatchDialog";
import { listOrderIdsForFilters } from "@/lib/admin/orders-bulk.functions";
import {
  DATE_PRESETS,
  DATE_PRESET_LABEL,
  PAYMENT_STATUS_FILTERS,
  PAYMENT_STATUS_LABEL,
  resolvePresetRange,
  type DatePreset,
} from "@/lib/admin/orders-bulk.schemas";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";

import { AdminSection } from "@/components/admin/AdminSection";

import { Pager } from "@/components/admin/Pager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOrders, useOrderNeighbourhoods } from "@/hooks/useOrders";
import {
  updateOrder,
  getMp2QuoteSummary,
  getMp2ConfigLabels,
  getMp2OrderSchedule,
  getTiffinOrderSummary,
  setTiffinMealSlot,
  type TiffinOrderSummary,
  type OrderRow,
  type Mp2QuoteSummary,
  type Mp2ConfigLabels,
  type Mp2OrderSchedule,
} from "@/lib/admin/orders.functions";
import {
  listOrderNotifications,
  retryOrderNotification,
  type OrderNotificationRow,
} from "@/lib/admin/order-notifications.functions";
import { listOrderPayments, type OrderPaymentRow } from "@/lib/admin/order-payments.functions";
import { useQuery } from "@tanstack/react-query";
import { OrderStatusTimeline } from "@/components/admin/OrderStatusTimeline";
import { OrderPaymentDetails } from "@/components/admin/OrderPaymentDetails";
import { OrderRefundPanel } from "@/components/admin/OrderRefundPanel";

import { OrderPrintActions } from "@/components/admin/OrderPrintActions";
import { OrderNotesHistory } from "@/components/admin/OrderNotesHistory";
import { OrderItemsSection } from "@/components/admin/OrderItemsSection";
import { OrderQuickActions } from "@/components/admin/OrderQuickActions";
import { OrdersSummaryCards } from "@/components/admin/OrdersSummaryCards";
import {
  toneClass,
  orderStatusTone,
  paymentStatusTone,
  kitchenStatusTone,
  kitchenStatusLabel,
  paymentMethodLabel,
} from "@/lib/admin/order-badges";


import {
  MEAL_CATEGORY_OPTIONS,
  MEAL_TYPE_OPTIONS,
  ORDER_STATUS_OPTIONS,
  type MealCategory,
  type MealType,
  type OrderStatus,
} from "@/lib/admin/orders.schemas";

export const Route = createFileRoute("/_admin/admin/orders")({
  component: OrdersPage,
  head: () => ({ meta: [
    { title: "Orders — Poonam Kapur Healthy Kitchen" },
    { name: "description", content: "Review saved customer orders, booking details and payment history." },
    { property: "og:title", content: "Orders — Poonam Kapur Healthy Kitchen" },
    { property: "og:description", content: "Administrative customer order and booking details." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
});

import { formatISTDate, formatISTDateTime } from "@/lib/date/ist";

function formatDate(iso: string | null): string {
  return formatISTDateTime(iso);
}

function statusLabel(s: OrderStatus): string {
  return ORDER_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

function categoryLabel(c: MealCategory): string {
  return MEAL_CATEGORY_OPTIONS.find((o) => o.value === c)?.label ?? c;
}

function sourceLabel(s: string | null): string {
  return s === "mp2" ? "MP2" : "Legacy";
}
function sourceVariant(s: string | null): "default" | "secondary" | "outline" | "destructive" {
  return s === "mp2" ? "default" : "outline";
}
function envLabel(e: string | null): string {
  if (e === "live") return "Live";
  if (e === "test") return "Test";
  return "—";
}
function envVariant(e: string | null): "default" | "secondary" | "outline" | "destructive" {
  if (e === "live") return "default";
  if (e === "test") return "secondary";
  return "outline";
}

function statusVariant(s: OrderStatus): "default" | "secondary" | "outline" | "destructive" {
  switch (s) {
    case "delivered":
      return "default";
    case "confirmed":
    case "preparing":
    case "out_for_delivery":
      return "secondary";
    case "cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

function formatInr(paise: number | null | undefined): string {
  if (paise == null) return "—";
  return `₹${(paise / 100).toFixed(2)}`;
}

function paymentVariant(s: string | null): "default" | "secondary" | "outline" | "destructive" {
  switch (s) {
    case "captured":
      return "default";
    case "authorized":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

function paymentLabel(s: string | null): string {
  if (s == null) return "—";
  return String(s).replace(/_/g, " ");
}

/** Filters survive the admin session so the production queue stays put. */
const FILTER_KEY = "admin.orders.production-filters";

type StoredFilters = {
  preset: DatePreset;
  customDate: string;
  customFrom: string;
  customTo: string;
  status: string;
  category: string;
  mealType: string;
  neighbourhood: string;
  orderSource: string;
  entryMode: string;
  paymentStatus: string;
  isDemo: boolean;
  origin: "production" | "review" | "test";
};

const DEFAULT_FILTERS: StoredFilters = {
  preset: "today",
  customDate: "",
  customFrom: "",
  customTo: "",
  status: "all",
  category: "all",
  mealType: "all",
  neighbourhood: "all",
  orderSource: "all",
  entryMode: "all",
  paymentStatus: "all",
  isDemo: false,
  origin: "production",
};

function readStoredFilters(): StoredFilters {
  if (typeof window === "undefined") return DEFAULT_FILTERS;
  try {
    const raw = window.sessionStorage.getItem(FILTER_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<StoredFilters>;
    return {
      ...DEFAULT_FILTERS,
      ...parsed,
      isDemo: typeof parsed.isDemo === "boolean" ? parsed.isDemo : DEFAULT_FILTERS.isDemo,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function OrdersPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFiltersState] = useState<StoredFilters>(readStoredFilters);
  const [selected, setSelected] = useState<OrderRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dispatchOpen, setDispatchOpen] = useState(false);

  const setFilters = (patch: Partial<StoredFilters>) => {
    setPage(1);
    setFiltersState((prev) => {
      const next = { ...prev, ...patch };
      try {
        window.sessionStorage.setItem(FILTER_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — filters simply don't persist */
      }
      return next;
    });
  };

  const {
    preset,
    customDate,
    customFrom,
    customTo,
    status,
    category,
    mealType,
    neighbourhood,
    orderSource,
    entryMode,
    paymentStatus,
    isDemo,
    origin,
  } = filters;

  const range = useMemo(
    () => resolvePresetRange(preset, customDate, customFrom, customTo),
    [preset, customDate, customFrom, customTo],
  );
  const rangeLabel =
    range.from === range.to ? range.from : `${range.from} → ${range.to}`;

  const queryInput = {
    search,
    status: status === "all" ? undefined : (status as OrderStatus),
    mealCategory: category === "all" ? undefined : (category as MealCategory),
    mealType: mealType === "all" ? undefined : (mealType as MealType),
    neighbourhood: neighbourhood === "all" ? undefined : neighbourhood,
    orderSource: orderSource === "all" ? undefined : orderSource,
    entryMode: entryMode === "all" ? undefined : entryMode,
    paymentStatus: paymentStatus === "all" ? undefined : paymentStatus,
    dateFrom: range.from,
    dateTo: range.to,
    isDemo,
    origin,
  };

  const query = useOrders({ page, ...queryInput });
  const neighbourhoodsQuery = useOrderNeighbourhoods();
  const idsFn = useServerFn(listOrderIdsForFilters);

  const pageRows = query.data?.rows ?? [];
  const pageIds = pageRows.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const togglePage = (on: boolean) => {
    setSelectedIds((prev) =>
      on
        ? [...new Set([...prev, ...pageIds])]
        : prev.filter((id) => !pageIds.includes(id)),
    );
  };

  const toggleOne = (id: string, on: boolean) => {
    setSelectedIds((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  };

  const selectAllMatching = async () => {
    try {
      const ids = await idsFn({ data: { page: 1, ...queryInput } });
      setSelectedIds(ids);
      toast.success(`${ids.length} order(s) selected`);
    } catch {
      toast.error("Couldn't select all matching orders.");
    }
  };

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div>
      <AdminPageHeader
        title={
          origin === "review"
            ? "Orders (Needs review)"
            : origin === "test"
              ? "Orders (Test – excluded)"
              : "Orders (Production)"
        }
        description={
          origin === "review"
            ? "Unresolved orders awaiting your production/test decision. Live-environment orders appear first."
            : origin === "test"
              ? "Verified test orders, preserved with full history and excluded from production operations."
              : "Verified production customer orders only."
        }
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-muted/50 rounded-lg p-1 mr-2 border">
              <Button
                variant={origin === "production" ? "default" : "ghost"}
                size="sm"
                className="h-7 text-[10px] px-2"
                onClick={() => setFilters({ origin: "production", isDemo: false })}
              >
                Production
              </Button>
              <Button
                variant={origin === "review" ? "default" : "ghost"}
                size="sm"
                className="h-7 text-[10px] px-2"
                onClick={() => setFilters({ origin: "review" })}
              >
                Needs review
              </Button>
              <Button
                variant={origin === "test" ? "default" : "ghost"}
                size="sm"
                className="h-7 text-[10px] px-2"
                onClick={() => setFilters({ origin: "test", isDemo: true })}
              >
                Test – excluded
              </Button>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-muted/30 mr-2">
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {query.isFetching ? "Refreshing..." : `Updated ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
              </span>
              <div className="h-4 w-px bg-border mx-1" />
              <div className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                <History className="h-3 w-3" />
                Auto refresh 30s
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 ml-1"
                onClick={() => void query.refetch()}
                disabled={query.isFetching}
              >
                <History className={`h-3.5 w-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
                <span className="sr-only">Refresh</span>
              </Button>
            </div>
            <AdminDataToolbar
              exportName="orders"
              moduleId="orders"
              onImported={() => void query.refetch()}
              rows={() =>
                (query.data?.rows ?? []).map((r) => ({
                  "Order Ref": r.order_ref ?? "",
                  "Customer Name": r.customer_name ?? "",
                  Phone: r.phone ?? "",
                  Email: r.email ?? "",
                  Status: r.status ?? "",
                  "Delivery Date": r.preferred_delivery_date ?? "",
                  "Delivery Time": r.preferred_delivery_time ?? "",
                  "Delivery Address": r.delivery_address ?? "",
                  Pincode: (r as { pincode?: string | null }).pincode ?? "",
                  "Special Instructions": r.special_instructions ?? "",
                  "Admin Notes": r.admin_notes ?? "",
                }))
              }
            />
          </div>
        }
      />


      <OrdersSummaryCards isDemo={isDemo} dateFrom={range.from} dateTo={range.to} />



      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Order date</span>
          {DATE_PRESETS.map((p) => (
            <Button
              key={p}
              type="button"
              size="sm"
              variant={preset === p ? "default" : "outline"}
              onClick={() => setFilters({ preset: p })}
            >
              {DATE_PRESET_LABEL[p]}
            </Button>
          ))}
          {preset === "custom_date" ? (
            <Input
              type="date"
              aria-label="Custom delivery date"
              className="w-[160px]"
              value={customDate}
              onChange={(e) => setFilters({ customDate: e.target.value })}
            />
          ) : null}
          {preset === "custom_range" ? (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="From date"
                className="w-[150px]"
                value={customFrom}
                onChange={(e) => setFilters({ customFrom: e.target.value })}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                aria-label="To date"
                className="w-[150px]"
                value={customTo}
                onChange={(e) => setFilters({ customTo: e.target.value })}
              />
            </div>
          ) : null}
          <Badge variant="secondary" className="ml-1">{rangeLabel}</Badge>
        </div>
        <form
          className="flex w-full max-w-md gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            applySearch();
          }}
        >
          <Input
            placeholder="Search ref, name, email, phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
          {search ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setPage(1);
              }}
            >
              Clear
            </Button>
          ) : null}
        </form>
        <div className="flex flex-wrap gap-2">
          <Select value={status} onValueChange={(v) => setFilters({ status: v })}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ORDER_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={(v) => setFilters({ category: v })}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Meal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All meals</SelectItem>
              {MEAL_CATEGORY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={neighbourhood} onValueChange={(v) => setFilters({ neighbourhood: v })}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Neighbourhood" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All neighbourhoods</SelectItem>
              {(neighbourhoodsQuery.data ?? []).map((n) => (
                <SelectItem key={n} value={n}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={orderSource} onValueChange={(v) => setFilters({ orderSource: v })}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Order source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {ORDER_SOURCE_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entryMode} onValueChange={(v) => setFilters({ entryMode: v })}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Entry mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entry modes</SelectItem>
              {ENTRY_MODES.map((m) => (
                <SelectItem key={m} value={m}>
                  {entryModeLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={mealType} onValueChange={(v) => setFilters({ mealType: v })}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Meal type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All meal types</SelectItem>
              {MEAL_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={paymentStatus} onValueChange={(v) => setFilters({ paymentStatus: v })}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Payment status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payment states</SelectItem>
              {PAYMENT_STATUS_FILTERS.map((v) => (
                <SelectItem key={v} value={v}>
                  {PAYMENT_STATUS_LABEL[v] ?? v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <OrdersBulkToolbar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds([])}
        rangeLabel={rangeLabel}
        onAssignDispatch={() => setDispatchOpen(true)}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={allPageSelected}
            onCheckedChange={(c) => togglePage(c === true)}
            aria-label="Select visible page"
          />
          Select visible page
        </label>
        <Button type="button" size="sm" variant="outline" onClick={() => void selectAllMatching()}>
          Select all matching filters
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={selectedIds.length === 0}
          onClick={() => setSelectedIds([])}
        >
          Clear selection
        </Button>
        <span className="text-muted-foreground">
          Selected Orders: <span className="tabular-nums">{selectedIds.length}</span>
        </span>
      </div>

      {query.isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Couldn't load orders. Please refresh and try again.</AlertDescription>
        </Alert>
      ) : null}

      {origin === "review" ? (
        <OrderOriginReviewPanel />
      ) : query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : query.data && query.data.rows.length > 0 ? (
        <>
          <div className="hidden overflow-x-auto rounded-md border md:block">
            <Table className="min-w-[1720px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={allPageSelected}
                      onCheckedChange={(c) => togglePage(c === true)}
                      aria-label="Select all on this page"
                    />
                  </TableHead>
                  <TableHead>Order No</TableHead>
                  <TableHead>Order Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Delivery Slot</TableHead>
                  <TableHead>Meal Type</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Kitchen Status</TableHead>
                  <TableHead>Order Status</TableHead>
                  <TableHead>Order Source</TableHead>
                  <TableHead>Entry Mode</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                  <TableHead className="text-right">View</TableHead>

                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.rows.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer align-top"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(r.id)}
                        onCheckedChange={(c) => toggleOne(r.id, c === true)}
                        aria-label={`Select order ${r.order_ref}`}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      {r.order_ref}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(r.created_at)}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium text-foreground">{r.customer_name}</div>
                      {r.email ? (
                        <div className="text-xs text-muted-foreground">{r.email}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">
                      {r.phone}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{r.neighbourhood}</div>
                      {r.pincode ? (
                        <div className="text-xs text-muted-foreground">{r.pincode}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.order_type === "tiffin" ? (r.tiffin_booking?.delivery_dates.map(formatISTDate).join(", ") || "Not recorded") : (r.preferred_delivery_date || "—")}
                      <br />
                      {r.order_type === "tiffin" ? (r.tiffin_booking?.delivery_windows.join(" / ") || "Not recorded") : (r.preferred_delivery_time ? r.preferred_delivery_time.slice(0, 5) : "—")}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      <Badge variant="outline">{r.order_type === "tiffin" ? (r.tiffin_booking?.meals.map((s) => s[0].toUpperCase() + s.slice(1)).join(" + ") || "Not recorded") : categoryLabel(r.meal_category)}</Badge>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {orderDietaryLabel(r.order_type, r.meal_type)}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[260px] text-sm">
                      <Badge variant="outline" className="mb-1">{orderTypeLabel(r.order_type)}</Badge>
                      <span className="line-clamp-2">{r.order_type === "tiffin" ? r.tiffin_booking?.plan_title : r.meal_plan_title}</span>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{r.quantity}</TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
                      {formatInr(r.total_amount_paise ?? r.payment_amount_paise)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={toneClass(paymentStatusTone(r.payment_status))}
                      >
                        {paymentLabel(r.payment_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      <div>{paymentMethodLabel(r)}</div>
                      <div className="text-muted-foreground">{envLabel(r.payment_environment)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={toneClass(kitchenStatusTone(r.status))}>
                        {kitchenStatusLabel(r.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={toneClass(orderStatusTone(r.status))}>
                        {statusLabel(r.status)}
                      </Badge>
                      <div className="mt-1">
                        <Badge variant={sourceVariant(r.order_source)} className="text-[10px]">
                          {sourceLabel(r.order_source)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      <Badge variant="secondary" className="text-[11px]">
                        {orderSourceLabel(r.order_source)}
                      </Badge>
                      {r.manual_payment_method ? (
                        <div className="mt-1 text-muted-foreground">
                          {manualPaymentLabel(r.manual_payment_method)}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      <Badge
                        variant={r.entry_mode === "admin" ? "default" : "outline"}
                        className="text-[11px]"
                      >
                        {entryModeLabel(r.entry_mode)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <OrderQuickActions order={r} onView={() => setSelected(r)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(r);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5" aria-hidden />
                        View
                      </Button>
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="space-y-3 md:hidden">
            {query.data.rows.map((r) => (
              <li
                key={r.id}
                className="cursor-pointer rounded-md border p-3"
                onClick={() => setSelected(r)}
              >
                <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 text-xs text-muted-foreground">
                  <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(r.id)}
                      onCheckedChange={(c) => toggleOne(r.id, c === true)}
                      aria-label={`Select order ${r.order_ref}`}
                    />
                  </span>
                  <span className="truncate font-mono">{r.order_ref}</span>
                  <Badge
                    variant="outline"
                    className={`shrink-0 ${toneClass(orderStatusTone(r.status))}`}
                  >
                    {statusLabel(r.status)}
                  </Badge>
                </div>
                <div className="mt-1 text-sm font-medium">{r.customer_name}</div>
                <div className="text-xs text-muted-foreground">
                  {r.phone} · {r.neighbourhood}
                  {r.pincode ? ` · ${r.pincode}` : ""}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {r.quantity}× {categoryLabel(r.meal_category)} ·{" "}
                  {formatInr(r.total_amount_paise ?? r.payment_amount_paise)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Delivery {r.preferred_delivery_date || "—"} {r.preferred_delivery_time ? r.preferred_delivery_time.slice(0, 5) : "—"}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={toneClass(paymentStatusTone(r.payment_status))}
                  >
                    {paymentLabel(r.payment_status)}
                  </Badge>
                  <Badge variant="outline" className={toneClass(kitchenStatusTone(r.status))}>
                    {kitchenStatusLabel(r.status)}
                  </Badge>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <OrderQuickActions order={r} onView={() => setSelected(r)} />
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(r);
                    }}
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden />
                    View
                  </Button>
                </div>

              </li>
            ))}
          </ul>

          <Pager
            page={query.data.page}
            pageSize={query.data.pageSize}
            total={query.data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No orders match these filters.
        </p>
      )}

      <OrdersBulkDispatchDialog
        open={dispatchOpen}
        onOpenChange={setDispatchOpen}
        selectedIds={selectedIds}
        onDone={() => setSelectedIds([])}
      />

      <OrderDrawer order={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function OrderDrawer({ order, onClose }: { order: OrderRow | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const updateFn = useServerFn(updateOrder);

  const initial = useMemo(
    () => ({
      status: (order?.status ?? "new") as OrderStatus,
      notes: order?.admin_notes ?? "",
    }),
    [order?.id, order?.status, order?.admin_notes],
  );

  const [status, setStatus] = useState<OrderStatus>(initial.status);
  const [notes, setNotes] = useState<string>(initial.notes);

  const editorKey = order?.id ?? "";
  const [lastKey, setLastKey] = useState<string>(editorKey);
  if (editorKey !== lastKey) {
    setLastKey(editorKey);
    setStatus(initial.status);
    setNotes(initial.notes);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!order) return;
      await updateFn({
        data: { id: order.id, status, admin_notes: notes },
      });
    },
    onSuccess: () => {
      toast.success("Order updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
      onClose();
    },
    onError: () => toast.error("Couldn't save changes. Please try again."),
  });

  // Closing the panel throws away edits, so ask first when something changed.
  const dirty = status !== initial.status || notes !== initial.notes;
  const requestClose = () => {
    if (dirty && !window.confirm("Discard your unsaved changes to this order?")) return;
    onClose();
  };

  const fetchMp2Schedule = useServerFn(getMp2OrderSchedule);
  const { data: schedule, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ["order-mp2-schedule", order?.id],
    queryFn: () => fetchMp2Schedule({ data: { orderId: order!.id } }),
    enabled: !!order && (order.order_type === "meal_plan" || ((order.order_source === "mp2" || order.order_source === "cart") && !!order.meal_plan_title)),
  });
  const fetchTiffinSummary = useServerFn(getTiffinOrderSummary);
  const { data: tiffinSummary } = useQuery({
    queryKey: ["order-tiffin-summary", order?.id],
    queryFn: () => order ? fetchTiffinSummary({ data: { orderId: order.id } }) : null,
    enabled: !!order && order.order_type === "tiffin",
  });

  // Admin correction of a mistaken Tiffin meal slot (lunch <-> dinner only).
  const saveMealSlot = useServerFn(setTiffinMealSlot);
  const [mealEditing, setMealEditing] = useState(false);
  const [mealDraft, setMealDraft] = useState<"lunch" | "dinner">("lunch");
  const savedMealSlot = tiffinSummary?.meals.length === 1 ? tiffinSummary.meals[0] : null;
  const mealMutation = useMutation({
    mutationFn: async () => {
      if (!order) return;
      await saveMealSlot({ data: { orderId: order.id, slot: mealDraft } });
    },
    onSuccess: () => {
      toast.success("Meal updated");
      setMealEditing(false);
      queryClient.invalidateQueries({ queryKey: ["order-tiffin-summary", order?.id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dispatch"] });
    },
    onError: () => toast.error("The meal could not be updated. Please try again."),
  });

  return (
    <Sheet open={order !== null} onOpenChange={(open) => (open ? null : requestClose())}>

      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-none md:w-[75vw] lg:w-[65vw]">
        {order ? (
          <>
            <SheetHeader className="border-b px-5 py-4">
              <SheetTitle className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{order.order_ref}</span>
                <Badge variant="outline" className={toneClass(orderStatusTone(order.status))}>
                  {statusLabel(order.status)}
                </Badge>
                <Badge variant="outline" className={toneClass(kitchenStatusTone(order.status))}>
                  {kitchenStatusLabel(order.status)}
                </Badge>
<Badge variant="outline">{orderTypeLabel(order.order_type)}</Badge>
                <Badge variant={sourceVariant(order.order_source)}>
                  {sourceLabel(order.order_source)}
                </Badge>
                <Badge variant={envVariant(order.payment_environment)}>
                  {envLabel(order.payment_environment)}
                </Badge>
              </SheetTitle>
              <SheetDescription>
                Received {formatDate(order.created_at)} · Last updated{" "}
                {formatDate(order.updated_at)}
              </SheetDescription>
            </SheetHeader>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <AdminSection
                number={1}
                icon={Receipt}
                title="Order summary"
                description="Reference, channel and totals at a glance."
              >
                <dl className="grid gap-3 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Order no</dt>
                    <dd className="font-mono text-xs">{order.order_ref}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Received</dt>
                    <dd>{formatDate(order.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Amount</dt>
                    <dd className="font-medium tabular-nums">
                      {formatInr(order.total_amount_paise ?? order.payment_amount_paise)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Order source</dt>
                    <dd>{orderSourceLabel(order.order_source)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Entry mode</dt>
                    <dd>{entryModeLabel(order.entry_mode)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Payment method</dt>
                    <dd>{paymentMethodLabel(order)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Order type</dt>
                    <dd className="font-medium">{orderTypeLabel(order.order_type)}</dd>
                  </div>
                  {order.order_type === "tiffin" && tiffinSummary && (
                    <>
                      <div>
                        <dt className="text-xs text-muted-foreground">Plan</dt>
                        <dd>{tiffinSummary.plan_title}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Duration</dt>
                        <dd>
                          {tiffinSummary.service_days
                            ? `${tiffinSummary.service_days} Day${tiffinSummary.service_days === 1 ? "" : "s"}`
                            : (tiffinSummary.duration_label ?? "—")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Meal</dt>
                        <dd>
                          {mealEditing ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <Select value={mealDraft} onValueChange={(v) => setMealDraft(v as "lunch" | "dinner")}>
                                <SelectTrigger className="h-8 w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="lunch">Lunch</SelectItem>
                                  <SelectItem value="dinner">Dinner</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                className="h-8"
                                disabled={mealMutation.isPending || mealDraft === savedMealSlot}
                                onClick={() => mealMutation.mutate()}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                disabled={mealMutation.isPending}
                                onClick={() => setMealEditing(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              {tiffinSummary.meals.map((s) => s[0].toUpperCase() + s.slice(1)).join(" + ") || "—"}
                              {savedMealSlot && (
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="Edit meal"
                                  onClick={() => {
                                    setMealDraft(savedMealSlot as "lunch" | "dinner");
                                    setMealEditing(true);
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Dietary preference</dt>
                        <dd>{tiffinSummary.dietary_preference}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Delivery time</dt>
                        <dd>{tiffinSummary.delivery_windows.join(" / ") || "Not recorded"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Delivery date</dt>
                        <dd>{tiffinSummary.delivery_dates.map(formatISTDate).join(", ") || "—"}</dd>
                      </div>
                    </>
                  )}
                  {order.order_type === "meal_plan" && schedule?.subscription && (
                    <>
                      <div>
                        <dt className="text-xs text-muted-foreground">Plan</dt>
                        <dd>{schedule.subscription.plan_title}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Duration</dt>
                        <dd>{schedule.subscription.duration_label}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Start date</dt>
                        <dd>{formatISTDate(schedule.subscription.start_date)}</dd>
                      </div>
                    </>
                  )}
                  {order.order_type === "a_la_carte" && order.preferred_delivery_date && (
                    <div>
                      <dt className="text-xs text-muted-foreground">Delivery date</dt>
                      <dd>{formatISTDate(order.preferred_delivery_date)}</dd>
                    </div>
                  )}
                </dl>
              </AdminSection>

              {order.gst_enabled_snapshot && (
                <AdminSection
                  number={1.5}
                  icon={Receipt}
                  title="Tax Breakdown"
                  description={`GST snapshot: ${order.gst_rate_snapshot}% ${order.gst_price_mode_snapshot}`}
                >
                  <dl className="grid gap-3 text-sm sm:grid-cols-3">
                    <div>
                      <dt className="text-xs text-muted-foreground">Taxable amount</dt>
                      <dd className="tabular-nums">{formatInr(order.gst_taxable_amount_paise ?? 0)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">GST ({order.gst_rate_snapshot}%)</dt>
                      <dd className="tabular-nums">{formatInr(order.gst_amount_paise ?? 0)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Order total</dt>
                      <dd className="font-bold tabular-nums">
                        {formatInr(order.total_amount_paise ?? order.payment_amount_paise ?? 0)}
                      </dd>
                    </div>
                    {order.wallet_applied_paise && order.wallet_applied_paise > 0 ? (
                      <div>
                        <dt className="text-xs text-muted-foreground">Wallet used</dt>
                        <dd className="tabular-nums text-primary">-{formatInr(order.wallet_applied_paise)}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="text-xs text-muted-foreground">Paid amount</dt>
                      <dd className="font-bold tabular-nums text-green-600">
                        {formatInr(
                          (order.total_amount_paise ?? order.payment_amount_paise ?? 0) - 
                          (order.wallet_applied_paise ?? 0)
                        )}
                      </dd>
                    </div>
                  </dl>
                </AdminSection>
              )}


              <AdminSection
                number={2}
                icon={CircleDot}
                title="Current status"
                description="Set the order status. Changes save with the Save button below."
                customised={status !== initial.status}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={toneClass(orderStatusTone(order.status))}>
                    {statusLabel(order.status)}
                  </Badge>
                  <Badge variant="outline" className={toneClass(kitchenStatusTone(order.status))}>
                    {kitchenStatusLabel(order.status)}
                  </Badge>
                </div>
                <div className="mt-3 max-w-xs">
                  <label htmlFor="order-status" className="text-xs text-muted-foreground">
                    Set status
                  </label>
                  <Select value={status} onValueChange={(v) => setStatus(v as OrderStatus)}>
                    <SelectTrigger id="order-status" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AdminSection>

              <AdminSection
                number={3}
                icon={User}
                title="Customer"
                description="Who placed this order and how to reach them."
              >
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <div className="min-w-0 break-words">
                    <dt className="text-xs text-muted-foreground">Name</dt>
                    <dd>{order.customer_name}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-muted-foreground">Phone</dt>
                    <dd className="tabular-nums">{order.phone}</dd>
                  </div>
                  <div className="min-w-0 break-all sm:col-span-2">
                    <dt className="text-xs text-muted-foreground">Email</dt>
                    <dd>{order.email || "—"}</dd>
                  </div>
                </dl>
              </AdminSection>

              <AdminSection
                number={4}
                icon={MapPin}
                title="Delivery information"
                description="Address and the requested delivery slot."
              >
                <address className="whitespace-pre-wrap text-sm not-italic">
                  {order.address_line_1 ? (
                    <>
                      <div>{order.address_line_1}</div>
                      {order.address_line_2 ? <div>{order.address_line_2}</div> : null}
                      {order.landmark ? <div className="text-xs text-muted-foreground italic">Landmark: {order.landmark}</div> : null}
                      <div>
                        {order.neighbourhood}
                        {order.pincode ? ` — ${order.pincode}` : ""}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>{order.delivery_address}</div>
                      {order.delivery_zone_name_snapshot && (
                        <div className="mt-2 p-2 bg-muted/50 rounded-md border border-border">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase font-bold text-muted-foreground">Delivery Zone</span>
                            <span className="text-xs font-semibold">{order.delivery_zone_name_snapshot}</span>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] uppercase font-bold text-muted-foreground">Delivery Charge</span>
                            <span className="text-xs font-semibold">{formatInr(order.delivery_charge_snapshot_paise)}</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </address>
                {order.order_type === "tiffin" ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Slot: {tiffinSummary?.delivery_dates.map(formatISTDate).join(", ") || "Not recorded"} · {tiffinSummary?.delivery_windows.join(" / ") || "Not recorded"}
                  </p>
                ) : order.preferred_delivery_time ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Slot: {order.preferred_delivery_date || "—"} at{" "}
                    {order.preferred_delivery_time.slice(0, 5)}
                  </p>
                ) : (
                  <p className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive">
                    Delivery time not chosen by customer — confirm before dispatch.
                  </p>
                )}
              </AdminSection>

              <AdminSection
                number={5}
                icon={UtensilsCrossed}
                title="Meal information"
                description="Plan, preference and quantity requested."
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline">{order.order_type === "tiffin" ? (tiffinSummary?.meals.map((s) => s[0].toUpperCase() + s.slice(1)).join(" + ") || "Not recorded") : categoryLabel(order.meal_category)}</Badge>
                  <span className="min-w-0 break-words">{order.order_type === "tiffin" ? tiffinSummary?.plan_title : order.meal_plan_title}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Preference: {orderDietaryLabel(order.order_type, order.meal_type)} · Qty {order.quantity}
                </p>
                {order.special_instructions ? (
                  <p className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm">
                    {order.special_instructions}
                  </p>
                ) : null}
              </AdminSection>

              <AdminSection
                number={6}
                icon={ListOrdered}
                title="Items ordered"
                description="Every line on this order, with plan configuration where relevant."
              >
                <div className="space-y-4">
                  <OrderItemsSection order={order} />
                  
                  {((order.order_source === "mp2" || order.order_source === "cart") && order.meal_plan_title) && (
                    <>
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <Utensils className="h-4 w-4" />
                          DISHES TO DELIVER
                        </h4>
                        
                        {schedule && schedule.ledger && schedule.ledger.length > 0 ? (
                          <div className="rounded-md border overflow-hidden">
                            <Table>
                              <TableHeader className="bg-muted/50">
                                <TableRow>
                                  <TableHead className="h-9 text-[10px] uppercase font-bold">Service Date</TableHead>
                                  <TableHead className="h-9 text-[10px] uppercase font-bold">Meal Slot</TableHead>
                                  <TableHead className="h-9 text-[10px] uppercase font-bold">Dish</TableHead>
                                  <TableHead className="h-9 text-[10px] uppercase font-bold text-right">Qty</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {schedule.ledger.flatMap((day) => 
                                  day.meals.map((meal, idx) => (
                                    <TableRow key={`${day.date}-${meal.slot}-${idx}`} className="h-10">
                                      <TableCell className="text-xs py-2">
                                        {new Date(day.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </TableCell>
                                      <TableCell className="text-xs py-2 capitalize">
                                        {(meal.slot ?? "—").replace(/_/g, " ")}
                                      </TableCell>
                                      <TableCell className="text-xs py-2 font-medium">
                                        {meal.dish_name}
                                      </TableCell>
                                      <TableCell className="text-xs py-2 text-right tabular-nums">1</TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground bg-muted/5">
                            {isLoadingSchedule ? "Loading delivery schedule..." : "No dish assignments found for this order."}
                          </div>
                        )}
                      </div>

                      <Mp2ConfigSection order={order} />
                      {order.mp2_quote_id ? (
                        <LinkedQuoteSection quoteId={order.mp2_quote_id} />
                      ) : null}
                    </>
                  )}
                </div>
              </AdminSection>

              <AdminSection
                number={7}
                icon={ChefHat}
                title="Kitchen details"
                description="Move the order forward one step at a time."
              >
                <OrderQuickActions order={order} onView={() => {}} />
              </AdminSection>

              <AdminSection
                number={8}
                icon={History}
                title="Timeline"
                description="Every status change with actor and timestamp."
              >
                <OrderStatusTimeline orderId={order.id} />
              </AdminSection>

              <AdminSection
                number={9}
                icon={CreditCard}
                title="Payment"
                description="Gateway references, amounts and refund details."
              >
                <OrderPaymentDetails order={order} />
                <div className="mt-4">
                  <OrderRefundPanel orderId={order.id} />
                </div>
                <div className="mt-4 p-3 rounded-lg border bg-muted/20 space-y-2">
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-muted-foreground">Food Subtotal</span>
                    <span>{formatInr(order.gst_taxable_amount_paise || order.total_amount_paise)}</span>
                  </div>
                  {order.gst_enabled_snapshot && (
                    <div className="flex justify-between py-1 text-sm">
                      <span className="text-muted-foreground">GST ({order.gst_rate_snapshot}%)</span>
                      <span>{formatInr(order.gst_amount_paise)}</span>
                    </div>
                  )}
                  {order.delivery_charge_snapshot_paise > 0 && (
                    <div className="flex justify-between py-1 text-sm">
                      <span className="text-muted-foreground">Delivery</span>
                      <span>{formatInr(order.delivery_charge_snapshot_paise)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t font-bold">
                    <span>Grand Total</span>
                    <span>{formatInr(order.total_amount_paise)}</span>
                  </div>
                </div>
              </AdminSection>

              {order.order_source === "mp2" && schedule?.subscription && (
                <AdminSection
                  number={12}
                  icon={Layers}
                  title="Meal Plan Details"
                  description="Authoritative plan and configuration."
                >
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Plan Name</dt>
                      <dd className="font-medium text-primary">{schedule.subscription.plan_title}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Combination</dt>
                      <dd>{schedule.subscription.combination_label}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Duration</dt>
                      <dd>{schedule.subscription.duration_label}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Food Preference</dt>
                      <dd>{schedule.subscription.preference_label}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Start Date</dt>
                      <dd>{schedule.subscription.start_date !== "—" 
                        ? new Date(schedule.subscription.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Total Meals / Service Days</dt>
                      <dd>{schedule.subscription.total_meals} Days</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Delivery Schedule</dt>
                      <dd>{schedule.subscription.delivery_rule_label}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wider">Delivery Area</dt>
                      <dd>{schedule.subscription.delivery_area_label}</dd>
                    </div>
                  </dl>
                </AdminSection>
              )}




              <AdminSection
                number={10}
                icon={Truck}
                title="Courier / dispatch notifications"
                description="Delivery and customer notifications sent for this order."
              >
                <NotificationHistory orderId={order.id} />
              </AdminSection>

              <AdminSection
                number={11}
                icon={StickyNote}
                title="Internal notes"
                description="Visible to staff only. Saved with the Save button below."
                customised={notes !== initial.notes}
              >
                <OrderNotesHistory notes={notes} onChange={setNotes} />
              </AdminSection>

              <AdminSection
                number={12}
                icon={Printer}
                title="Printing"
                description="Kitchen, packing, delivery and summary documents."
              >
                <OrderPrintActions order={order} />
              </AdminSection>
            </div>


            <SheetFooter className="flex-row justify-end gap-2 border-t px-5 py-3">
              <Button
                type="button"
                variant="outline"
                onClick={requestClose}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
            </SheetFooter>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

const NOTIF_KIND_LABEL: Record<OrderNotificationRow["kind"], string> = {
  admin_new_order: "Admin — new order",
  customer_order_received: "Customer — order received",
  customer_status_updated: "Customer — status updated",
};

function notifStatusVariant(
  s: OrderNotificationRow["status"],
): "default" | "secondary" | "outline" | "destructive" {
  switch (s) {
    case "sent":
      return "default";
    case "processing":
    case "pending":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

function NotificationHistory({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listOrderNotifications);
  const retryFn = useServerFn(retryOrderNotification);
  const q = useQuery({
    queryKey: ["admin", "order-notifications", orderId],
    queryFn: () => listFn({ data: { orderId } }),
    staleTime: 5_000,
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => retryFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Notification queued for retry");
      queryClient.invalidateQueries({ queryKey: ["admin", "order-notifications", orderId] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Retry failed";
      toast.error(msg);
    },
  });

  return (
    <section className="mt-6 border-t pt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Email notifications
      </h3>
      {q.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <p className="text-xs text-destructive">Couldn't load notification history.</p>
      ) : (q.data ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">No emails queued for this order yet.</p>
      ) : (
        <ul className="space-y-2">
          {(q.data ?? []).map((n) => (
            <li key={n.id} className="rounded-md border p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{NOTIF_KIND_LABEL[n.kind]}</span>
                <Badge variant={notifStatusVariant(n.status)}>{n.status}</Badge>
              </div>
              <div className="mt-1 text-muted-foreground">To {n.recipient}</div>
              {n.status_snapshot ? (
                <div className="text-muted-foreground">Status: {n.status_snapshot}</div>
              ) : null}
              <div className="text-muted-foreground">
                Attempts: {n.attempt_count}/{n.max_attempts}
                {n.manual_retry_count > 0 ? ` · Manual retries: ${n.manual_retry_count}/3` : ""}
              </div>
              {n.sent_at ? (
                <div className="text-muted-foreground">Sent {formatDate(n.sent_at)}</div>
              ) : null}
              {n.last_error ? (
                <div className="mt-1 text-destructive">Error: {n.last_error}</div>
              ) : null}
              {n.skipped_reason ? (
                <div className="mt-1 text-muted-foreground">Skipped: {n.skipped_reason}</div>
              ) : null}
              {n.status === "failed" && n.manual_retry_count < 3 ? (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={retryMutation.isPending}
                    onClick={() => retryMutation.mutate(n.id)}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type Mp2LineItem = { label?: string; amount_paise?: number };
function readString(o: unknown, path: string[]): string | null {
  let cur: unknown = o;
  for (const k of path) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else return null;
  }
  return typeof cur === "string" ? cur : null;
}
function readArray(o: unknown, path: string[]): unknown[] | null {
  let cur: unknown = o;
  for (const k of path) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else return null;
  }
  return Array.isArray(cur) ? (cur as unknown[]) : null;
}

function Mp2ConfigSection({ order }: { order: OrderRow }) {
  const snap = order.configuration_snapshot as unknown;
  if (!snap) return null;
  const planId = readString(snap, ["plan_id"]);
  const comboId = readString(snap, ["combination_id"]);
  const durationId = readString(snap, ["duration_id"]);
  const ruleId = readString(snap, ["delivery_rule_id"]);
  const areaId = readString(snap, ["delivery_area_id"]);
  const prefCode = readString(snap, ["food_preference_code"]);

  const labelsFn = useServerFn(getMp2ConfigLabels);
  const labelsQ = useQuery<Mp2ConfigLabels>({
    queryKey: ["admin", "mp2-labels", planId, comboId, durationId, ruleId, areaId, prefCode],
    queryFn: () =>
      labelsFn({
        data: {
          plan_id: planId,
          combination_id: comboId,
          duration_id: durationId,
          delivery_rule_id: ruleId,
          delivery_area_id: areaId,
          food_preference_code: prefCode,
        },
      }),
    staleTime: 60_000,
    enabled: Boolean(planId || comboId || durationId || ruleId || areaId || prefCode),
  });
  const labels = labelsQ.data;

  const plan = labels?.plan?.title ?? labels?.plan?.slug ?? readString(snap, ["plan_title"]);
  const combo = labels?.combination?.label ?? labels?.combination?.code ?? null;
  const pref = labels?.food_preference?.label ?? prefCode;
  const duration =
    labels?.duration?.label ??
    (labels?.duration?.code
      ? `${labels.duration.code}${labels.duration.total_service_days ? ` (${labels.duration.total_service_days}d)` : ""}`
      : null);
  const rule = labels?.delivery_rule?.label ?? labels?.delivery_rule?.code ?? null;
  const area =
    (labels?.delivery_area?.neighbourhood
      ? `${labels.delivery_area.neighbourhood}${labels.delivery_area.pincode ? ` — ${labels.delivery_area.pincode}` : ""}`
      : null) ?? order.neighbourhood;
  const requested = readString(snap, ["requested_start_date"]);
  const effective = readString(snap, ["schedule", "start_date"]);
  const serviceDays =
    readArray(snap, ["schedule", "service_days"]) ?? readArray(snap, ["schedule", "service_dates"]);
  const items = (readArray(snap, ["pricing", "line_items"]) ?? []) as Mp2LineItem[];
  const quoteExpires = readString(snap, ["quote", "expires_at"]);
  const serviceDaysStr =
    serviceDays && serviceDays.length > 0
      ? (() => {
          const strs = serviceDays.filter((d): d is string => typeof d === "string");
          if (strs.length === 0) return `${serviceDays.length} days`;
          const first = strs[0];
          const last = strs[strs.length - 1];
          return first === last
            ? `${strs.length} day (${first})`
            : `${strs.length} days (${first} — ${last})`;
        })()
      : "—";
  return (
    <section className="rounded-md border p-3 text-sm">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        MP2 configuration
      </h3>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        <div>
          <span className="text-muted-foreground">Plan:</span> {plan ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Combination:</span> {combo ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Preference:</span> {pref ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Duration:</span> {duration ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Delivery rule:</span> {rule ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Area:</span> {area ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Requested start:</span> {requested ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Effective start:</span> {effective ?? "—"}
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">Service dates:</span> {serviceDaysStr}
        </div>
        {quoteExpires ? (
          <div className="col-span-2">
            <span className="text-muted-foreground">Quote expiry:</span> {formatDate(quoteExpires)}
          </div>
        ) : null}
      </div>
      {items.length > 0 ? (
        <div className="mt-2">
          <div className="text-xs font-semibold text-muted-foreground">Line items</div>
          <ul className="mt-1 space-y-1 text-xs">
            {items.map((li, idx) => (
              <li key={idx} className="flex justify-between">
                <span>{li.label ?? "Item"}</span>
                <span className="tabular-nums">{formatInr(li.amount_paise ?? null)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-muted-foreground">Raw snapshot</summary>
        <pre className="mt-1 max-h-60 overflow-auto rounded bg-muted/40 p-2 text-[10px]">
          {JSON.stringify(snap, null, 2)}
        </pre>
      </details>
    </section>
  );
}

function LinkedQuoteSection({ quoteId }: { quoteId: string }) {
  const fn = useServerFn(getMp2QuoteSummary);
  const q = useQuery<Mp2QuoteSummary | null>({
    queryKey: ["admin", "mp2-quote", quoteId],
    queryFn: () => fn({ data: { quoteId } }),
    staleTime: 30_000,
  });
  return (
    <section className="rounded-md border p-3 text-sm">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Linked quote
      </h3>
      {q.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : q.isError || !q.data ? (
        <p className="text-xs text-muted-foreground">Quote not available.</p>
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <div className="col-span-2 truncate">
            <span className="text-muted-foreground">Quote ID:</span>{" "}
            <span className="font-mono">{q.data.id}</span>
          </div>
          {q.data.parent_quote_id ? (
            <div className="col-span-2 truncate">
              <span className="text-muted-foreground">Parent:</span>{" "}
              <span className="font-mono">{q.data.parent_quote_id}</span>
            </div>
          ) : null}
          <div>
            <span className="text-muted-foreground">Status:</span> {q.data.status}
          </div>
          <div>
            <span className="text-muted-foreground">Amount:</span>{" "}
            {formatInr(q.data.total_amount_paise)} {q.data.currency ?? ""}
          </div>
          <div>
            <span className="text-muted-foreground">Created:</span> {formatDate(q.data.quoted_at)}
          </div>
          <div>
            <span className="text-muted-foreground">Expires:</span> {formatDate(q.data.expires_at)}
          </div>
        </div>
      )}
    </section>
  );
}
